import { FC } from 'react';
import { Redirect } from 'react-router-dom';
import { VStack } from '@chakra-ui/react';

import Callout from 'components/_core/Callout';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import { StepProps } from 'components/Stepper/types';
import useToast from 'components/ToastProvider/useToast';
import { formatAddress } from 'helpers/address';
import { VAT_RATE } from 'helpers/amount';
import {
  BASIC_CARD_NAME,
  PREMIUM_CARD_NAME,
} from 'helpers/paymentCards/constants';
import * as Layout from 'layouts/StepperLayout';

import * as routes from '../../../../../routes';
import { CtaButton } from '../../../components/Buttons';
import { useOrderPhysicalPaymentCard } from '../../hooks/useOrderPhysicalPaymentCard';
import { usePhysicalCardOrderFlowContext } from '../../hooks/usePhysicalCardOrderFlowContext';
import locales from './locales';

const RecapItemValue: typeof SunshineCard.Text = (props) => (
  <SunshineCard.Text
    fontWeight="weight-500"
    size="md"
    variant="primary"
    {...props}
  />
);

const RecapItemText: typeof SunshineCard.Text = (props) => (
  <SunshineCard.Text size="md" {...props} />
);

const PhysicalCardOrderRecap: FC<StepProps> = ({ goBack, goNext }) => {
  const { showToast } = useToast();

  const [
    orderPhysicalCard,
    { error: cardOrderingError, loading: cardOrderingLoading },
  ] = useOrderPhysicalPaymentCard();

  const { flowConfig, setSharedState, sharedState } =
    usePhysicalCardOrderFlowContext();
  const {
    bankAccount: { bankAccountId, nextBalance },
    cardType,
    coordinates: { fullName, personalAddress, professionalAddress },
    pricingWithoutVAT: { cardPrice, monthlyFee },
  } = flowConfig;
  const { alias, pin, shouldSendToCompanyAddress } = sharedState;
  const deliveryAddress = shouldSendToCompanyAddress
    ? professionalAddress
    : personalAddress;
  const cardOrderTotalPriceWithVat = (cardPrice + monthlyFee) * VAT_RATE;
  const isBalanceSufficient = cardOrderTotalPriceWithVat <= nextBalance;

  const onNextPressed = async () => {
    try {
      const { data } = await orderPhysicalCard({
        alias: alias || undefined,
        bankAccountId,
        pin,
        shouldSendToCompanyAddress: !!shouldSendToCompanyAddress,
      });

      if (data) {
        const { creditCardId } = data.orderPhysicalPaymentCard.creditCard;
        setSharedState({
          ...sharedState,
          createdCardId: creditCardId,
        });

        goNext();
      }
    } catch (err) {
      showToast({
        message: locales.orderErrorMessage,
        type: 'error',
      });
      <Redirect to={routes.card} />;
    }
  };

  const cardName = cardType === 'basic' ? BASIC_CARD_NAME : PREMIUM_CARD_NAME;
  const deliveryAddressFormatted = `${fullName}, ${formatAddress(
    deliveryAddress,
  )}`;

  return (
    <Layout.Root>
      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton isDisabled={cardOrderingLoading} onClick={goBack} />
        </Layout.HeaderButtonsContainer>

        <Layout.BodyScrollableSection>
          <VStack alignItems="stretch" spacing="space-8">
            <Heading size="lg">{locales.title}</Heading>
            <Text size="lg">{locales.subtitle}</Text>
          </VStack>

          <VStack spacing="space-24">
            <SunshineCard.Group marginTop="space-32">
              <SunshineCard>
                <SunshineCard.Content>
                  <RecapItemText>
                    {locales.formatString(locales.cardName, cardName)}
                  </RecapItemText>
                </SunshineCard.Content>

                <SunshineCard.Slot name="action">
                  <RecapItemValue>
                    {monthlyFee
                      ? locales.formatString(locales.pricePerMonth, monthlyFee)
                      : locales.priceFree}
                  </RecapItemValue>
                </SunshineCard.Slot>
              </SunshineCard>

              <SunshineCard>
                <SunshineCard.Content>
                  <RecapItemText>{locales.labelDeliveryTime}</RecapItemText>
                </SunshineCard.Content>

                <SunshineCard.Slot name="action">
                  <RecapItemValue fontWeight="weight-400">
                    {locales.deliveryTime}
                  </RecapItemValue>
                </SunshineCard.Slot>
              </SunshineCard>

              {cardPrice ? (
                <SunshineCard>
                  <SunshineCard.Content>
                    <RecapItemText>
                      {locales.labelCardAdditionalPrice}
                    </RecapItemText>
                  </SunshineCard.Content>

                  <SunshineCard.Slot name="action">
                    <RecapItemValue>
                      {locales.formatString(locales.price, cardPrice)}
                    </RecapItemValue>
                  </SunshineCard.Slot>
                </SunshineCard>
              ) : null}

              <SunshineCard>
                <SunshineCard.Content>
                  <RecapItemText>{locales.labelShippingPrice}</RecapItemText>
                </SunshineCard.Content>

                <SunshineCard.Slot name="action">
                  <RecapItemText>{locales.priceFree}</RecapItemText>
                </SunshineCard.Slot>
              </SunshineCard>
            </SunshineCard.Group>

            <SunshineCard
              onClick={() => {
                goNext({ key: 'add-delivery-info' });
              }}
            >
              <SunshineCard.Content>
                <SunshineCard.Title>
                  {locales.addressInfoBoxTitle}
                </SunshineCard.Title>

                <SunshineCard.Text>
                  {deliveryAddressFormatted}
                </SunshineCard.Text>
              </SunshineCard.Content>

              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
            </SunshineCard>
          </VStack>

          {cardOrderTotalPriceWithVat > 0 ? (
            <Text
              marginTop="space-16"
              paddingLeft="space-16"
              size="sm"
              variant="light"
            >
              {locales.vatMention}
            </Text>
          ) : null}

          {!isBalanceSufficient ? (
            <Callout marginTop="space-32" variant="error">
              <Callout.Content>
                {locales.errorBoxNotEnoughFunds}
              </Callout.Content>
            </Callout>
          ) : null}

          {cardOrderingError ? (
            <Callout marginTop="space-32" variant="error">
              <Callout.Content>{locales.orderErrorMessage}</Callout.Content>
            </Callout>
          ) : null}
        </Layout.BodyScrollableSection>

        <Layout.FooterContainer>
          <StepperProgressBar />
          <Layout.FooterButtonsContainer>
            <CtaButton
              isDisabled={!isBalanceSufficient}
              isLoading={cardOrderingLoading}
              onClick={onNextPressed}
            >
              {locales.orderCta}
            </CtaButton>
          </Layout.FooterButtonsContainer>
        </Layout.FooterContainer>
      </Layout.Body>
    </Layout.Root>
  );
};

export default PhysicalCardOrderRecap;
